// Shared logic for deciding whether a drug movement requires a double (witnessed) signature.
//
// Rules:
// - Schedule 8 always requires a double signature and is not configurable.
// - Schedule 4 and S4Vic are defaults only. The backend applies them until a facility saves
//   its own tag list; from then on that list is the authority, so a facility that deliberately
//   drops S4 no longer requires a witness for it. facility.double_signature_drugs is therefore
//   already the complete answer and always contains S8 - see Facility::getControlledDrugTags().
// - Facilities can also nominate their own tags to extend double-signature requirements to
//   other drugs held in the drug safe, for local security/safety reasons. Those are treated
//   exactly like an S8 for signing purposes.
// - Tag comparisons are case-insensitive - upstream data is inconsistent about "S8" vs "s8".
// - Tags may arrive as plain strings ("S8") or objects ({ name: "S8" }) depending on the
//   endpoint; both shapes are normalised here.
const ALWAYS_DUAL_TAGS = ['s8'];
// Used only when there is no facility list to consult at all (facility not loaded), where
// failing safe matters more than honouring a configuration we can't see.
const DEFAULT_DUAL_TAGS = ['s8', 's4', 's4vic'];

function normaliseTags(tags) {
    if (!Array.isArray(tags)) return [];
    return tags.map(t => String(t?.name ?? t).toLowerCase());
}

// Extracts the tag list from a drug/stock item in any of the shapes used across the app:
//   { tags: [...] }, { alias: { tags } }, { stock_item: { alias: { tags } } },
//   { stockItem: { alias: { tags } } }
// Falls through to the next shape if a candidate is missing OR empty - an empty array is
// truthy in JS, so `drug.tags || drug.alias.tags` would otherwise short-circuit on a stale/
// empty `tags: []` and silently ignore real tags present in a nested shape (fail-open on a
// safety-critical check). Non-array values are ignored defensively.
function extractTags(drug) {
    const candidates = [drug?.tags, drug?.alias?.tags, drug?.stock_item?.alias?.tags, drug?.stockItem?.alias?.tags];
    for (const tags of candidates) {
        if (Array.isArray(tags) && tags.length) return tags;
    }
    return [];
}

// Case-insensitive, shape-tolerant check for a single tag (e.g. tagsInclude(tags, 'insulin')).
export function tagsInclude(tags, tagName) {
    return normaliseTags(tags).includes(String(tagName).toLowerCase());
}

// Returns the full set of tags (lowercased) that require a double signature at this facility.
// The facility's own list is authoritative (it always carries S8); the defaults apply only when
// we have no list to read.
export function getDoubleSigTags(facility) {
    const facilityTags = normaliseTags(facility?.double_signature_drugs);
    if (!facilityTags.length) return DEFAULT_DUAL_TAGS;
    return [...new Set([...ALWAYS_DUAL_TAGS, ...facilityTags])];
}

// Returns true if any of the given tags require a double signature at this facility.
export function tagsRequireDoubleSig(tags, facility) {
    const dualTags = getDoubleSigTags(facility);
    return normaliseTags(tags).some(tag => dualTags.includes(tag));
}

// Returns true if any drug in the given list requires a double signature at this facility.
export function drugsRequireDoubleSig(drugs, facility) {
    if (!Array.isArray(drugs) || !drugs.length) return false;
    const dualTags = getDoubleSigTags(facility);
    return drugs.some(drug => normaliseTags(extractTags(drug)).some(tag => dualTags.includes(tag)));
}
